import { useQuery } from '@tanstack/react-query';
import type { EntityOptionsResponse } from '@releve/shared';
import { authedGet } from '@/lib/api';
import { captureException } from '@/lib/sentry';
import type { EntityCellConfig, EntityTableConfig } from '@/components/ui/editable-table/types';

export interface EntityOption {
  value: string;
  label: string;
}

/**
 * Fetch the options of one table through
 * `GET /api/v1/entity-options/orgs/:orgId/:table` — the complete, slim
 * `{ id, …labelColumns }` list (keyset-paged past PostgREST's 1,000-row cap).
 * The server decides org vs global scope and RLS decides visibility; the
 * table's registry filters (`additionalFilters`) travel as query params.
 */
async function fetchTableOptions(
  tableConfig: EntityTableConfig,
  organizationId: string,
): Promise<EntityOption[]> {
  try {
    const params = new URLSearchParams();
    Object.entries(tableConfig.additionalFilters ?? {}).forEach(([key, value]) => {
      params.set(key, String(value));
    });
    const query = params.toString();
    const { items } = await authedGet<EntityOptionsResponse>(
      `/api/v1/entity-options/orgs/${organizationId}/${tableConfig.table}${query ? `?${query}` : ''}`,
    );

    const fields = tableConfig.displayField.split(',').map((f) => f.trim());
    const valuePrefix = tableConfig.valuePrefix || '';

    return items
      .map((row) => ({
        value: valuePrefix + row.id,
        // Multiple fields (e.g. 'first_name,last_name') join with a space.
        label: fields.map((f) => row[f]).filter(Boolean).join(' '),
      }))
      // The keyset read returns uuid order — sort for the dropdown.
      .sort((a, b) => a.label.localeCompare(b.label));
  } catch (err) {
    captureException(err instanceof Error ? err : new Error(String(err)), {
      context: 'EntityOptions',
      action: 'fetchTableOptions',
      table: tableConfig.table,
    });
    return [];
  }
}

/**
 * Fetch entity options for a config (single table, or a `tables[]` union such
 * as the `b:`/`p:` payee columns).
 */
async function fetchEntityOptions(
  config: EntityCellConfig,
  organizationId: string,
): Promise<EntityOption[]> {
  if (config.tables && config.tables.length > 0) {
    const allOptions = await Promise.all(
      config.tables.map((tableConfig) => fetchTableOptions(tableConfig, organizationId)),
    );
    return allOptions.flat();
  }
  return fetchTableOptions(config, organizationId);
}

/**
 * Generate a stable query key for the entity options
 */
function getQueryKey(config: EntityCellConfig, organizationId: string | undefined): string[] {
  if (config.tables && config.tables.length > 0) {
    const tablesKey = config.tables
      .map((t) => `${t.table}:${t.valuePrefix || ''}:${JSON.stringify(t.additionalFilters || {})}`)
      .join('|');
    return ['entityOptions', 'multi', tablesKey, organizationId ?? ''];
  }
  return [
    'entityOptions',
    config.table,
    organizationId ?? '',
    JSON.stringify(config.additionalFilters || {}),
  ];
}

/**
 * React Query hook to fetch entity options for dropdown cells. Disabled until
 * both a config and an `organizationId` are present.
 *
 * Uses React Query for:
 * - Automatic caching (5 min stale time)
 * - Request deduplication
 * - Cache invalidation on mutations (the `['entityOptions']` prefix)
 */
export function useEntityOptions(
  config: EntityCellConfig | undefined,
  organizationId: string | undefined,
) {
  return useQuery({
    queryKey: config ? getQueryKey(config, organizationId) : ['entityOptions', 'disabled'],
    queryFn: () => {
      if (!config || !organizationId) return [];
      return fetchEntityOptions(config, organizationId);
    },
    enabled: !!config && !!organizationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (garbage collection)
  });
}

/**
 * Hook to fetch options for multiple entity configs at once
 * Returns a map of config key -> options
 */
export function useMultipleEntityOptions(
  configs: Array<{ key: string; config: EntityCellConfig }>,
  organizationId: string | undefined,
) {
  // Create a unique key for this combination of configs
  const configsKey = configs.map(c => `${c.key}:${getQueryKey(c.config, organizationId).join(':')}`).join('|');

  return useQuery({
    queryKey: ['entityOptions', 'multiple', configsKey, organizationId ?? ''],
    queryFn: async () => {
      if (!organizationId) return {} as Record<string, EntityOption[]>;
      const results = await Promise.all(
        configs.map(async ({ key, config }) => ({
          key,
          options: await fetchEntityOptions(config, organizationId),
        }))
      );

      return results.reduce((acc, { key, options }) => {
        acc[key] = options;
        return acc;
      }, {} as Record<string, EntityOption[]>);
    },
    enabled: configs.length > 0 && !!organizationId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}
